import { formatISO } from "date-fns"
import { useApi } from "src/hooks/api"
import { queryClient } from "./client"
import {
  getQueryKey,
  useDeleteQuery,
  useGetQuery,
  usePatchQuery,
  usePostQuery,
  useRouteParams
} from "./helpers"
import { ManualInvoiceParams, OrganizationParams } from "./types"

//--------------------------------------------------------------------------------------------------
// URLs
//--------------------------------------------------------------------------------------------------

/** @returns /manage/billing/accounts/ */
export const getBillingAccountListUrl = () => "/manage/billing/accounts/"

/** @returns /manage/billing/accounts/${orgUuid}/?org=${orgUuid} */
export const getBillingAccountUrl = ({ orgUuid }: Required<OrganizationParams>) =>
  `/manage/billing/accounts/${orgUuid}/?org=${orgUuid}`

/** @returns /billing/plans/current/?org=${orgUuid} */
export const getBillingCurrentPlanUrl = ({ orgUuid }: Required<OrganizationParams>) =>
  `/billing/plans/current/?org=${orgUuid}`

/** @returns /manage/billing/accounts/${orgUuid}/plan/?org=${orgUuid} */
export const getBillingPlanUrl = ({ orgUuid }: Required<OrganizationParams>) =>
  `/manage/billing/accounts/${orgUuid}/plan/?org=${orgUuid}`

/** @returns /manage/billing/accounts/${orgUuid}/service/?org=${orgUuid} */
export const getBillingServiceUrl = ({ orgUuid }: Required<OrganizationParams>) =>
  `/manage/billing/accounts/${orgUuid}/service/?org=${orgUuid}`

/** @returns /sales/billing/refill/?org=${orgUuid} */
export const getBillingRefillUrl = ({ orgUuid }: Required<OrganizationParams>) =>
  `/sales/billing/refill/?org=${orgUuid}`

/** @returns /manage/billing/accounts/${orgUuid}/subscription/?org=${orgUuid} */
export const getBillingSubscriptionUrl = ({ orgUuid }: Required<OrganizationParams>) =>
  `/manage/billing/accounts/${orgUuid}/subscription/?org=${orgUuid}`

/** @returns /manage/billing/accounts/${orgUuid}/manual_invoices/?org=${orgUuid} */
export const getBillingManualInvoiceListUrl = ({ orgUuid }: Required<OrganizationParams>) =>
  `/manage/billing/accounts/${orgUuid}/manual_invoices/?org=${orgUuid}`

/** @returns /manage/billing/accounts/${orgUuid}/manual_invoices/?org=${orgUuid} */
export const getBillingManualInvoiceUrl = ({ orgUuid, reference }: Required<ManualInvoiceParams>) =>
  `/manage/billing/accounts/${orgUuid}/manual_invoices/${reference}/?org=${orgUuid}`

/** @returns /manage/billing/accounts/${orgUuid}/manual_invoices/?org=${orgUuid} */
export const getBillingManualInvoiceDownloadUrl = ({
  orgUuid,
  reference
}: Required<ManualInvoiceParams>) =>
  `/manage/billing/accounts/${orgUuid}/manual_invoices/${reference}/download/?org=${orgUuid}`

/** @returns /manage/billing/accounts/${orgUuid}/usage_reporter/connection_test/?org=${orgUuid} */
export const getBillingUsageReporterConnectionTestUrl = ({
  orgUuid
}: Required<OrganizationParams>) =>
  `/manage/billing/accounts/${orgUuid}/usage_reporting/connection_test/?org=${orgUuid}`

/** @returns /manage/billing/zuora/link_account/?org=${orgUuid} */
export const getZuoraLinkAccountUrl = ({ orgUuid }: Required<OrganizationParams>) =>
  `/manage/billing/zuora/link_account/?org=${orgUuid}`

/** @returns /manage/billing/accounts/${orgUuid}/prices?period=${period}&org=${orgUuid} */
export const getBillingPricesUrl = ({ orgUuid }: Required<OrganizationParams>, period: string) => {
  const qs = new URLSearchParams([
    ["org", orgUuid],
    ["period", period]
  ]).toString()
  return `/manage/billing/accounts/${orgUuid}/prices/?${qs}`
}

/** @returns /billing/accounts/subscriptions/periods?org=${orgUuid} */
export const getBillingPeriodsUrl = ({ orgUuid }: Required<OrganizationParams>) =>
  `/billing/accounts/subscriptions/periods/?org=${orgUuid}`

//--------------------------------------------------------------------------------------------------
// Queries & mutations
//--------------------------------------------------------------------------------------------------

export const useBillingAccountList = () => {
  const url = getBillingAccountListUrl()

  return useGetQuery<api.billing.PaginatedPrivateAccountList>("billing", url)
}

export const useBillingAccount = (params?: OrganizationParams) => {
  const mergedParams = useRouteParams(params)
  const url = getBillingAccountUrl(mergedParams)

  return useGetQuery<api.billing.PrivateDetailedAccount>("billing", url)
}

export const useBillingCurrentPlan = (params?: OrganizationParams) => {
  const mergedParams = useRouteParams(params)
  const url = getBillingCurrentPlanUrl(mergedParams)

  return useGetQuery<api.billing.Plan>("billing", url)
}

export const useUpdateBillingAccount = (params?: OrganizationParams) => {
  const mergedParams = useRouteParams(params)
  const url = getBillingAccountUrl(mergedParams)
  const listUrl = getBillingAccountListUrl()

  type TRequest = DeepPartial<api.billing.PatchedPrivateDetailedAccount>
  type TResponse = api.billing.PrivateDetailedAccount

  return usePatchQuery<TRequest, TResponse>("billing", url, [listUrl])
}

export const useRefillCredits = (params?: OrganizationParams) => {
  const mergedParams = useRouteParams(params)
  const url = getBillingRefillUrl(mergedParams)
  const accountUrl = getBillingAccountUrl(mergedParams)
  const accountListUrl = getBillingAccountListUrl()

  type TRequest = api.billing.Refill
  type TResponse = api.billing.Refill

  return usePostQuery<TRequest, TResponse>("billing", url, [accountUrl, accountListUrl])
}

export const useSubscribeToPlan = (params?: OrganizationParams) => {
  const { jsonPost } = useApi("billing")
  const mergedParams = useRouteParams(params)
  const url = getBillingPlanUrl(mergedParams)
  const accountUrl = getBillingAccountUrl(mergedParams)
  const accntListUrl = getBillingAccountListUrl()

  type TRequest = api.billing.PlanCreation
  type TResponse = api.billing.PlanCreation

  return {
    mutationFn: (data: TRequest) => jsonPost<TResponse>(url, data),
    onSuccess: async () => {
      await Promise.all([
        queryClient.refetchQueries({ exact: true, queryKey: getQueryKey("billing", accountUrl) }),
        queryClient.refetchQueries({ exact: true, queryKey: getQueryKey("billing", accntListUrl) })
      ])
    }
  }
}

export const useSubscribeToServiceLevel = (params?: OrganizationParams) => {
  const { jsonPost } = useApi("billing")
  const mergedParams = useRouteParams(params)
  const url = getBillingServiceUrl(mergedParams)
  const accountUrl = getBillingAccountUrl(mergedParams)
  const accntListUrl = getBillingAccountListUrl()

  type TRequest = api.billing.ServiceCreation
  type TResponse = api.billing.ServiceCreation

  return {
    mutationFn: (data: TRequest) => jsonPost<TResponse>(url, data),
    onSuccess: async () => {
      await Promise.all([
        queryClient.refetchQueries({ exact: true, queryKey: getQueryKey("billing", accountUrl) }),
        queryClient.refetchQueries({ exact: true, queryKey: getQueryKey("billing", accntListUrl) })
      ])
    }
  }
}

export const usePlayUsage = (range: { from: Date; to: Date }, params?: OrganizationParams) => {
  return useGetUsages<api.billing.PlayUsage>("/billing/usages/play", range, params)
}

export const useLinkUsage = (range: { from: Date; to: Date }, params?: OrganizationParams) => {
  return useGetUsages<api.billing.LinkUsage>("/billing/usages/link", range, params)
}

export const useSwitchUsage = (range: { from: Date; to: Date }, params?: OrganizationParams) => {
  return useGetUsages<api.billing.SwitchUsage>("/billing/usages/switch", range, params)
}

export const useIngestUsage = (range: { from: Date; to: Date }, params?: OrganizationParams) => {
  return useGetUsages<api.billing.IngestUsage>("/billing/usages/ingest", range, params)
}

/** @returns ${baseUrl}/?from=${from}&to=${to}&org=${orgUuid} */
export const getUsagesUrl = (
  baseUrl: string,
  range: { from: Date; to: Date },
  orgUuid?: string
) => {
  const { from, to } = range
  const qs = new URLSearchParams([
    ["from", formatISO(from, { representation: "date" })],
    ["to", formatISO(to, { representation: "date" })],
    ["org", orgUuid || ""]
  ]).toString()

  return `${baseUrl}/?${qs}`
}

const useGetUsages = <T>(
  base_url: string,
  range: { from: Date; to: Date },
  params?: OrganizationParams
) => {
  const mergedParams = useRouteParams(params)
  const url = getUsagesUrl(base_url, range, mergedParams.orgUuid)

  return useGetQuery<T>("billing", url)
}

export const getCogsOrganizationExportUrl = (startDate?: string, endDate?: string) => {
  const params = new URLSearchParams()
  if (startDate) params.set("start_date", startDate)
  if (endDate) params.set("end_date", endDate)
  const query = params.toString()
  return `/manage/billing/cogs/organization/export/${query ? `?${query}` : ""}`
}

export const useCogsDetail = (month?: string) => {
  const url = `/manage/billing/cogs/${month}/`

  return useGetQuery<api.billing.CogsDetail>("billing", url)
}

export const getCogsCostOverviewUrl = (startDate: string, endDate: string) =>
  `/manage/billing/cogs/cost/?start_date=${startDate}&end_date=${endDate}`

export const useCogsCostOverview = (startDate: string, endDate: string) => {
  const url = getCogsCostOverviewUrl(startDate, endDate)

  return useGetQuery<api.billing.CogsCostOverviewItem[]>("billing", url)
}

export const getCogsOrganizationCostUrl = (startDate?: string, endDate?: string) => {
  const params = new URLSearchParams()
  if (startDate) params.set("start_date", startDate)
  if (endDate) params.set("end_date", endDate)
  const query = params.toString()
  return `/manage/billing/cogs/organization/${query ? `?${query}` : ""}`
}

export const useCogsOrganizationCost = (startDate?: string, endDate?: string) => {
  const url = getCogsOrganizationCostUrl(startDate, endDate)
  return useGetQuery<api.billing.CogsOrganizationCost[]>("billing", url)
}

export const useManualInvoiceList = (params?: OrganizationParams) => {
  const mergedParams = useRouteParams(params)
  const url = getBillingManualInvoiceListUrl(mergedParams)

  return useGetQuery<api.billing.PaginatedManualInvoiceList>("billing", url)
}

export const useAddManualInvoice = (params?: OrganizationParams) => {
  const mergedParams = useRouteParams(params)
  const url = getBillingManualInvoiceListUrl(mergedParams)

  type TRequest = FormData
  type TResponse = api.billing.ManualInvoice & { uuid: string }

  return usePostQuery<TRequest, TResponse>("billing", url, [url], "form-data")
}

export const useDeleteManualInvoice = (params?: ManualInvoiceParams) => {
  const mergedParams = useRouteParams(params)
  const url = getBillingManualInvoiceUrl(mergedParams)

  return useDeleteQuery("billing", url, [getBillingManualInvoiceListUrl(mergedParams)])
}

export interface ConnectionTestResponse {
  uuid: string
  status: "success" | "failure"
  details: string
}

export const useUsageReporterTestConnection = (params?: OrganizationParams) => {
  const mergedParams = useRouteParams(params)
  const url = getBillingUsageReporterConnectionTestUrl(mergedParams)

  type TRequest = FormData
  return usePostQuery<TRequest, ConnectionTestResponse>("billing", url, [url], "form-data")
}

export const useZuoraLinkAccount = (params?: OrganizationParams) => {
  const { jsonPost } = useApi("billing")
  const mergedParams = useRouteParams(params)
  const url = getZuoraLinkAccountUrl(mergedParams)
  const accountUrl = getBillingAccountUrl(mergedParams)
  const accntListUrl = getBillingAccountListUrl()

  type TRequest = api.billing.ZuoraLinkAccount
  type TResponse = api.billing.ZuoraLinkAccount

  return {
    mutationFn: (data: TRequest) => jsonPost<TResponse>(url, data),
    onSuccess: async () => {
      await Promise.all([
        queryClient.refetchQueries({ exact: true, queryKey: getQueryKey("billing", accountUrl) }),
        queryClient.refetchQueries({ exact: true, queryKey: getQueryKey("billing", accntListUrl) })
      ])
    }
  }
}

export const useBillingPrices = (period: Date, params?: OrganizationParams) => {
  const mergedParams = useRouteParams(params)
  const periodStr = formatISO(period, { representation: "date" }).slice(0, 10)
  const url = getBillingPricesUrl(mergedParams, periodStr)
  return useGetQuery<api.billing.CostsFromZuora[]>("billing", url)
}

export const useBillingPeriods = (params?: OrganizationParams) => {
  const mergedParams = useRouteParams(params)
  const url = getBillingPeriodsUrl(mergedParams)
  return useGetQuery<api.billing.BillingPeriod[]>("billing", url)
}
